import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { EmailIframe } from '@/lib/email';
import type { TriggerEmailData } from '@/lib/proposals/core/types';
import { cx } from '@/utils/cx';

import {
  decodeHtmlEntities,
  extractSenderEmail,
  extractSenderName,
  formatRelativeTime,
  SenderAvatar,
  TimelineConnector,
} from './shared';

/**
 * Email trigger card (existing email trigger type) with expand/collapse.
 *
 * Composes the shared `<Accordion>` / `<AccordionItem>` so this card
 * inherits the smooth grid-rows open/close animation, react-aria keyboard
 * handling (Enter / Space toggle, focus management), and consistent
 * design-system focus styling. The trigger row holds the email header
 * (sender · subject · time + chevron) plus the snippet (visible only when
 * collapsed); the panel reveals the full email body (HTML iframe or plain
 * text) when expanded.
 */
function emailTriggerLabel({ isExpanded }: { isExpanded: boolean }) {
  return isExpanded ? 'Collapse email preview' : 'Expand email preview';
}

function renderEmailBody(email: TriggerEmailData['email']) {
  if (email.body_html) {
    return (
      <EmailIframe html={email.body_html} minHeight='60px' className='w-full' />
    );
  }
  return (
    <div className='text-xs text-secondary whitespace-pre-wrap'>
      {email.body}
    </div>
  );
}

export function EmailTriggerCard({
  trigger,
  onOpenThread,
  showConnector,
  defaultExpanded,
}: {
  trigger: TriggerEmailData;
  onOpenThread?: () => void;
  showConnector: boolean;
  defaultExpanded: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Lazy-mount the body so the email iframe (or large body div) only
  // renders once the user has expanded the card. Stays mounted after
  // first expand so collapse still animates from real content.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    if (isExpanded && !hasMounted) {
      setHasMounted(true);
    }
  }, [isExpanded, hasMounted]);

  const handleViewThreadClick = useCallback(
    (e: MouseEvent) => {
      // endContent renders OUTSIDE the trigger button (sibling, not child)
      // so a normal click on this button no longer bubbles into the
      // disclosure toggle. We still stop propagation defensively in case
      // the surrounding card layout ever wraps both back into a single
      // click target.
      e.stopPropagation();
      onOpenThread?.();
    },
    [onOpenThread]
  );

  const handleViewThreadKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onOpenThread?.();
      }
    },
    [onOpenThread]
  );

  const { email } = trigger;
  const senderName = extractSenderName(email.from);
  const senderEmail = extractSenderEmail(email.from);
  const relativeTime = formatRelativeTime(email.date);

  // Highlight "View thread" while an email-thread slideout is open — same
  // open-state affordance as the header's record buttons. A kind-presence
  // check (not an exact entry-id match) is enough: any email_thread entry on
  // the stack while this proposal's card is on screen was opened from this
  // proposal, and a thread opened from a stacked record slideout covers this
  // card with its backdrop anyway.
  const slideoutStack = useSlideoutStackContextOptional();
  const isThreadOpen = !!slideoutStack?.stack.some(
    entry => entry.kind === 'email_thread'
  );

  return (
    <div className='mb-0'>
      <div className='w-full rounded-xl bg-white dark:bg-bg-secondary border border-primary shadow-xs overflow-hidden'>
        {/* Standalone AccordionItem (no DisclosureGroup wrapper): a
            single controlled item honors `isExpanded` on mount, so
            `defaultExpanded` opens the card immediately. A DisclosureGroup
            would ignore the child's initial `isExpanded`. */}
        <AccordionItem isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
          {/* Overlay trigger pattern: AccordionTrigger fills the row
                via `absolute inset-0`, so clicking anywhere — except on
                the View Thread <button>, which re-enables
                `pointer-events-auto` — toggles the disclosure. */}
          <div className='relative'>
            <AccordionTrigger
              aria-label={emailTriggerLabel({ isExpanded })}
              className='absolute inset-0 w-full focus-visible:ring-inset rounded'
            >
              {null}
            </AccordionTrigger>

            {/* Visible content layer. Header row sits above the
                  collapsed-state snippet, which spans the full card
                  width below — indented past the avatar so it aligns
                  with the header text and extends out to the card's
                  right edge. */}
            <div className='pointer-events-none relative'>
              <div className='flex items-center gap-3 px-4 pt-4'>
                <div className='flex-shrink-0'>
                  <SenderAvatar email={senderEmail} name={senderName} />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-1.5 min-w-0 flex-1'>
                      <span className='text-sm font-medium text-primary flex-shrink-0'>
                        {senderName}
                      </span>
                      <span className='text-tertiary flex-shrink-0'>·</span>
                      <span className='text-sm text-secondary truncate'>
                        {email.subject}
                      </span>
                    </div>
                    <span className='text-xs text-tertiary flex-shrink-0'>
                      {relativeTime}
                    </span>
                  </div>
                </div>

                {onOpenThread && (
                  <button
                    type='button'
                    onClick={handleViewThreadClick}
                    onKeyDown={handleViewThreadKeyDown}
                    aria-pressed={isThreadOpen}
                    // No `transition-colors` here. With the overlay
                    // trigger pattern (this button sits over a
                    // `pointer-events-none` content layer above the
                    // disclosure trigger), even a 150ms colour
                    // transition reads as a delay between cursor
                    // entering the button and the hover bg appearing.
                    className={cx(
                      'pointer-events-auto shrink-0 self-start px-2 py-0.5 text-xs font-medium rounded-full focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500',
                      isThreadOpen
                        ? // Open state: solid brand, same as an active filter
                          // pill (GlobalSearch precedent).
                          'bg-brand-600 text-white hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-400'
                        : 'text-brand-600 bg-brand-50 dark:bg-brand-900/20 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-900/30'
                    )}
                  >
                    View thread
                  </button>
                )}

                <AccordionChevron
                  variant='default'
                  className='w-4 h-4 self-start'
                />
              </div>

              {/* Full-width collapsed-state snippet, indented past the
                    avatar (60px = 16px p-4 + 32px avatar + 12px gap) so
                    it aligns with the header text and extends to the
                    card's right edge — under where the trailing meta
                    sits in the header row above. */}
              {/* `truncate` clips to one line with the browser's
                    text-overflow ellipsis when the snippet overflows;
                    the trailing `…` keeps the affordance visible even
                    when the snippet happens to fit (the snippet is the
                    first ~140 chars of the email — there's almost
                    always more content in the expanded view).
                    Concatenated into a single text node so existing
                    test queries that match the raw snippet still find
                    it. */}
              {!isExpanded && (
                <div className='pl-[60px] pr-4 pb-4 pt-1 text-xs text-tertiary truncate'>
                  {`${decodeHtmlEntities(email.snippet)}…`}
                </div>
              )}
            </div>
          </div>

          <AccordionPanel className='px-4 pb-4'>
            {hasMounted && renderEmailBody(email)}
          </AccordionPanel>
        </AccordionItem>
        {/* Screen-reader live region — aria-expanded already announces
            state changes via the trigger button, but consumer tests rely
            on this human-readable phrasing too. */}
        <div className='sr-only' aria-live='polite' aria-atomic='true'>
          {isExpanded ? 'Email preview expanded' : 'Email preview collapsed'}
        </div>
      </div>
      {/* Connecting line to action below */}
      {showConnector && <TimelineConnector />}
    </div>
  );
}
